import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Timer, Users, Bell } from 'lucide-react';
import { useIsMobile } from '../hooks/use-mobile';

const SCREENS = [
  {
    title: "Set Your Timer",
    subtitle: "Choose destination, deadline & notes",
    icon: Timer,
    color: "from-emerald-500/20 to-teal-600/20",
    accent: "text-emerald-500",
    bgColor: "bg-emerald-50",
    features: ["Custom notes"],
    content: (
      <div className="space-y-3 p-4">
        <div className="h-3 w-24 bg-slate-200 rounded-full" />
        <div className="h-8 w-full bg-emerald-100 rounded-xl" />
        <div className="h-8 w-3/4 bg-slate-100 rounded-xl" />
        <div className="flex gap-2 mt-4">
          <div className="h-10 flex-1 bg-emerald-500 rounded-xl" />
          <div className="h-10 flex-1 bg-slate-100 rounded-xl" />
        </div>
      </div>
    )
  },
  {
    title: "Trust Circles",
    subtitle: "Add your emergency contacts",
    icon: Users,
    color: "from-blue-500/20 to-indigo-600/20",
    accent: "text-blue-500",
    bgColor: "bg-blue-50",
    features: ["Up to 5 groups", "7 contacts each"],
    content: (
      <div className="space-y-3 p-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-slate-200" />
            <div className="flex-1 space-y-1.5">
              <div className="h-2.5 w-20 bg-slate-200 rounded-full" />
              <div className="h-2 w-32 bg-slate-100 rounded-full" />
            </div>
            <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
              <div className="w-3 h-3 rounded-full bg-blue-500" />
            </div>
          </div>
        ))}
        <div className="h-10 w-full border-2 border-dashed border-slate-200 rounded-xl flex items-center justify-center">
          <div className="h-3 w-24 bg-slate-100 rounded-full" />
        </div>
      </div>
    )
  },
  {
    title: "Silent Protection",
    subtitle: "We monitor so you don't have to",
    icon: Bell,
    color: "from-amber-500/20 to-orange-600/20",
    accent: "text-amber-500",
    bgColor: "bg-amber-50",
    features: ["Cloud synced", "Battery independent", "48h link expiry"],
    content: (
      <div className="space-y-3 p-4">
        <div className="h-24 rounded-2xl bg-gradient-to-br from-amber-100 to-orange-50 flex items-center justify-center">
          <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center">
            <div className="w-3 h-3 rounded-full bg-amber-500 animate-pulse" />
          </div>
        </div>
        <div className="h-3 w-full bg-slate-100 rounded-full" />
        <div className="h-3 w-2/3 bg-slate-100 rounded-full" />
        <div className="h-10 w-full bg-amber-100 rounded-xl mt-2" />
      </div>
    )
  }
];

export default function AppShowcase() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });
  const isMobile = useIsMobile();

  return (
    <section id="app-showcase" ref={ref} className="relative py-32 md:py-40 bg-slate-50 overflow-hidden">
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-emerald-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 container mx-auto px-6 max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: isMobile ? 0 : 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: isMobile ? 0.4 : 0.8 }}
          className="text-center mb-20"
        >
          <span className="inline-block text-xs font-medium uppercase tracking-[0.2em] text-emerald-600 mb-4">
            The App
          </span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
            Safety in your <span className="text-emerald-600">pocket</span>
          </h2>
          <p className="text-lg text-slate-500 max-w-2xl mx-auto">
            Clean, intuitive, and designed for the moments that matter most. <br />
            Three screens. Zero confusion. Total peace of mind.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
          {SCREENS.map((screen, i) => (
            <motion.div
              key={screen.title}
              initial={{ opacity: 0, y: isMobile ? 0 : 60 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: isMobile ? 0.4 : 0.8, delay: isMobile ? 0 : i * 0.2 }}
              className="flex flex-col items-center"
            >
              <div className="relative w-[260px] h-[520px]">
                <div className="absolute inset-0 rounded-[3rem] bg-slate-900 p-2 shadow-2xl shadow-slate-900/30">
                  <div className="w-full h-full rounded-[2.5rem] bg-white overflow-hidden relative">
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-6 bg-slate-900 rounded-b-2xl z-10" />
                    <div className="pt-10 pb-4 px-2 h-full flex flex-col">
                      <div className={`h-20 rounded-2xl bg-gradient-to-br ${screen.color} flex items-center justify-center mb-4`}>
                        <screen.icon className={`w-8 h-8 ${screen.accent}`} />
                      </div>
                      {screen.content}
                    </div>
                  </div>
                </div>
                <div className="absolute inset-0 rounded-[3rem] bg-gradient-to-br from-white/10 to-transparent pointer-events-none" />
              </div>

              <div className="mt-8 text-center">
                <h3 className="text-lg font-bold text-slate-900 mb-1">{screen.title}</h3>
                <p className="text-sm text-slate-500 mb-3">{screen.subtitle}</p>
                {screen.features && (
                  <div className="flex flex-wrap justify-center gap-2">
                    {screen.features.map((feat, j) => (
                      <span key={j} className={`px-2.5 py-1 rounded-full text-[10px] font-semibold ${screen.bgColor} ${screen.accent} uppercase tracking-wider`}>
                        {feat}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
